import { useCallback, useContext, useState } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { DashboardContext } from '../contexts/DashboardContext';
import { DashboardsContext } from '../contexts/DashboardsContext';
import { GroupSettingsContext } from '../contexts/GroupSettingsContext';
import { addDashboardQueryFunc, updateDashboardFunc } from '../lib/helper';
import { Dashboard, DashboardQuery, DashboardQuerySource, RefreshDataEnum } from '../types/files';
import { AnswerQuestionResponse } from '../types/search';
import ObjectUtils from '../utils/ObjectUtils';

const useDashboard = () => {
  const { userGroup } = useContext(AuthContext);
  const { externalSync } = useContext(GroupSettingsContext);
  const { dashboards, setDashboards } = useContext(DashboardsContext);
  const { dashboard, dashboardQueriesRef, setDashboardQueries, setDashboard } = useContext(DashboardContext);

  const [loading, setLoading] = useState<boolean>(false);

  const updateTitle = useCallback(
    (value: string): boolean => {
      if (!dashboard) return false;

      if (dashboard.title === value) return true;

      if (dashboards.some((d) => d.title === value)) return false;

      const updatedDashboard = {
        ...dashboard,
        title: value,
        refreshData: {
          ...dashboard.refreshData,
          lastUpdatedBy: 'user',
          shouldRefresh: true,
          modifiedData: RefreshDataEnum.Title,
        },
      };

      updateDashboardFunc(updatedDashboard).then(() => {
        setDashboard(updatedDashboard);
        setDashboards((prev) =>
          prev.map((d) => (d.id === updatedDashboard.id ? updatedDashboard : d))
        );
      });

      return true;
    },
    [dashboard, dashboards, setDashboard, setDashboards]
  );

  const updateOwner = useCallback(
    (value: string): boolean => {
      if (!dashboard) return false;

      if (dashboard.userId === value) return true;

      const updatedDashboard = {
        ...dashboard,
        userId: value,
        refreshData: {
          ...dashboard.refreshData,
          lastUpdatedBy: 'user',
          shouldRefresh: true,
          modifiedData: RefreshDataEnum.Title,
        },
      };

      updateDashboardFunc(updatedDashboard).then(() => {
        setDashboard(updatedDashboard);
        setDashboards((prev) =>
          prev.map((d) => (d.id === updatedDashboard.id ? updatedDashboard : d))
        );
      });

      return true;
    },
    [dashboard, setDashboard, setDashboards]
  );

  const updateSource = useCallback(
    (value: string, type: string): boolean => {
      if (!dashboard) return false;

      if (dashboard.source === value) return true;

      const updatedDashboard = {
        ...dashboard,
        refreshData: {
          ...dashboard.refreshData,
          lastUpdatedBy: 'user',
          shouldRefresh: true,
          modifiedData: RefreshDataEnum.Title,
        },
      };

      if (type === 'name') {
        updatedDashboard.dealSource = { ...dashboard.dealSource, name: value };
        updatedDashboard.source = value;
      } else if (type === 'email') {
        updatedDashboard.dealSource = { ...dashboard.dealSource, email: value };
        updatedDashboard.source = dashboard.dealSource?.name || dashboard.source;
      }

      updateDashboardFunc(updatedDashboard).then(() => {
        setDashboard(updatedDashboard);
        setDashboards((prev) =>
          prev.map((d) => (d.id === updatedDashboard.id ? updatedDashboard : d))
        );
      });

      return true;
    },
    [dashboard, setDashboard, setDashboards]
  );

  const updateStage = useCallback(
    (value: string): boolean => {
      if (!dashboard) return false;

      if (dashboard.investmentStage === value) return true;

      const updatedDashboard = {
        ...dashboard,
        investmentStage: value,
        refreshData: {
          ...dashboard.refreshData,
          lastUpdatedBy: 'user',
          shouldRefresh: true,
          modifiedData: RefreshDataEnum.Title,
        },
      };

      updateDashboardFunc(updatedDashboard).then(() => {
        setDashboard(updatedDashboard);
        setDashboards((prev) =>
          prev.map((d) => (d.id === updatedDashboard.id ? updatedDashboard : d))
        );
      });

      return true;
    },
    [dashboard, setDashboard, setDashboards]
  );

  const updateTags = useCallback(
    (value: string[]): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        if (!dashboard) {
          resolve(false);
          return;
        }

        if (ObjectUtils.equalObjects(dashboard.tags, value)) {
          resolve(true);
          return;
        }

        const updatedDashboard = {
          ...dashboard,
          tags: value,
          refreshData: {
            ...dashboard.refreshData,
            lastUpdatedBy: 'user',
            shouldRefresh: true,
            modifiedData: RefreshDataEnum.Title,
          },
        };

        updateDashboardFunc(updatedDashboard).then(() => {
          setDashboard(updatedDashboard);
          setDashboards((prev) =>
            prev.map((d) => (d.id === updatedDashboard.id ? updatedDashboard : d))
          );
          resolve(true);
        });
      });
    },
    [dashboard, setDashboard, setDashboards]
  );

  const sharePublicly = useCallback(
    async (sharing: boolean, localDashboard?: Dashboard) => {
      const updatedDashboard = {
        ...(localDashboard ?? dashboard)!,
        isPublic: sharing,
        refreshData: {
          ...(localDashboard ?? dashboard)!.refreshData,
          lastUpdatedBy: 'user',
          shouldRefresh: true,
          modifiedData: RefreshDataEnum.Title,
        },
      };

      await updateDashboardFunc(updatedDashboard);
      setDashboard(updatedDashboard);
      setDashboards((prev) =>
        prev.map((d) => (d.id === updatedDashboard.id ? updatedDashboard : d))
      );
    },
    [dashboard, setDashboard, setDashboards]
  );

  const autoSync = useCallback(
    async (sync?: boolean) => {
      if (!dashboard) return;

      const updatedDashboard = {
        ...dashboard,
        shouldSyncExternally: sync ?? externalSync,
        refreshData: {
          ...dashboard.refreshData,
          lastUpdatedBy: 'user',
          shouldRefresh: true,
          modifiedData: RefreshDataEnum.Title,
        },
      };

      await updateDashboardFunc(updatedDashboard);
      setDashboard(updatedDashboard);
      setDashboards((prev) =>
        prev.map((d) => (d.id === updatedDashboard.id ? updatedDashboard : d))
      );
    },
    [dashboard, externalSync, setDashboard, setDashboards]
  );

  const modifyQuery = useCallback(
    (query: DashboardQuery, answer: AnswerQuestionResponse) => {
      if (!dashboard) return;

      setLoading(true);
      addDashboardQueryFunc(
        dashboard.id,
        query.title,
        userGroup,
        query.query,
        JSON.stringify({ answer: answer.answer, type: answer.type }),
        !!answer?.history ? JSON.stringify(answer.history) : query.history,
        query.order,
        true,
        DashboardQuerySource.MANUAL
      )
        .then((updatedQuery) => {
          dashboardQueriesRef.current = dashboardQueriesRef.current.map((q) =>
            q.title === updatedQuery.title ? updatedQuery : q
          );
          setDashboardQueries(dashboardQueriesRef.current.slice());
        })
        .finally(() => setLoading(false));
    },
    [dashboard, userGroup, dashboardQueriesRef, setDashboardQueries]
  );

  return {
    loading,
    updateTitle,
    updateOwner,
    updateSource,
    updateStage,
    updateTags,
    sharePublicly,
    autoSync,
    modifyQuery,
  };
};

export default useDashboard;